// src/belediye/services/reportApi.js
// Report V1 API wrapper (config/options/query/export) — BACKEND-CONTRACT ALIGNED
//
// Backend canonical param names (Query/Export/Options):
// - unit_ids (list[int]) + units_mode (all|selected)
// - neighborhoods (list[int])
// - statuses (list[str])
// - datasets (list[str])
// - columns (list[str])
// - ui_mode (simple|advanced)
// - primary_dataset
// - view_mode, date_mode, unit_scope, page, page_size, ordering, etc.

import belediyeApi from "./belediyeApi";

/* =============================================================================
   Logger
============================================================================= */

const DEBUG =
  (typeof process !== "undefined" &&
    process?.env?.NODE_ENV !== "production" &&
    process?.env?.REACT_APP_REPORT_DEBUG !== "0") ||
  false;

const log = (...args) => DEBUG && console.log("[ReportApi]", ...args);
const logError = (...args) => DEBUG && console.error("[ReportApi]", ...args);

const compact = (params) => {
  try {
    const out = {};
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined) return;

      if (typeof v === "string") {
        out[k] = v.length > 120 ? `${v.slice(0, 120)}…` : v;
        return;
      }

      if (Array.isArray(v)) {
        out[k] = v.length > 20 ? [...v.slice(0, 20), `…(+${v.length - 20})`] : v;
        return;
      }

      out[k] = v;
    });
    return out;
  } catch {
    return params;
  }
};

/* =============================================================================
   Helpers
============================================================================= */

const stripUndefined = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
};

// "__all__" sentinel: backend'e göndermiyoruz. "Tümü" => paramı omit et.
const ALL = "__all__";

const stripAllSentinel = (arr) =>
  Array.isArray(arr) ? arr.filter((x) => x !== ALL && x !== null && x !== undefined) : arr;

const uniqArr = (arr) => {
  if (!Array.isArray(arr)) return arr;

  const seen = new Set();
  const out = [];

  arr.forEach((x) => {
    if (x === null || x === undefined) return;
    const key = String(x);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(x);
  });

  return out;
};

const normalizeArray = (v) => {
  if (!Array.isArray(v)) return v;
  const cleaned = uniqArr(stripAllSentinel(v));
  return cleaned.length ? cleaned : null; // boş array => omit etmeyi kolaylaştır
};

const normalizeString = (v) => {
  if (typeof v !== "string") return v;
  const trimmed = v.trim();
  return trimmed || undefined;
};

const normalizeMethod = (value) => {
  const method = String(value || "get").trim().toLowerCase();
  return method === "post" ? "post" : "get";
};

/**
 * Backward-compat input aliases (INPUT ONLY):
 * - units -> unit_ids
 * - unit_ids -> unit_ids
 * - status -> statuses
 * - neighborhood -> neighborhoods
 * - operation_actions -> operation_action
 */
const normalizeQueryExportParams = (raw) => {
  const p = { ...(raw || {}) };

  // ---- Units (canonical: unit_ids) ----
  if (p.unit_ids === undefined && p.units !== undefined) p.unit_ids = p.units;
  delete p.units;

  // ---- Statuses (canonical: statuses) ----
  if (p.statuses === undefined && p.status !== undefined) p.statuses = p.status;
  delete p.status;

  // ---- Neighborhoods (canonical: neighborhoods) ----
  if (p.neighborhoods === undefined && p.neighborhood !== undefined) {
    p.neighborhoods = p.neighborhood;
  }
  delete p.neighborhood;

  // ---- Operation aliases (canonical: operation_action) ----
  if (p.operation_action === undefined && p.operation_actions !== undefined) {
    p.operation_action = p.operation_actions;
  }
  delete p.operation_actions;

  // ---- Normalize strings ----
  p.ui_mode = normalizeString(p.ui_mode);
  p.view_mode = normalizeString(p.view_mode);
  p.date_mode = normalizeString(p.date_mode);
  p.unit_scope = normalizeString(p.unit_scope);
  p.units_mode = normalizeString(p.units_mode);
  p.ordering = normalizeString(p.ordering);
  p.search = normalizeString(p.search);
  p.tracking_number = normalizeString(p.tracking_number);
  p.primary_dataset = normalizeString(p.primary_dataset);
  p.export_mode = normalizeString(p.export_mode);

  // ---- Clean arrays ----
  p.unit_ids = normalizeArray(p.unit_ids);
  p.neighborhoods = normalizeArray(p.neighborhoods);
  p.statuses = normalizeArray(p.statuses);
  p.datasets = normalizeArray(p.datasets);
  p.columns = normalizeArray(p.columns);
  p.operation_action = normalizeArray(p.operation_action);
  p.close_request_statuses = normalizeArray(p.close_request_statuses);

  // ---- UI-only keys should never go to backend ----
  delete p.advanced_datasets;
  delete p.openDropdown;
  delete p.selected_unit;
  delete p.selected_units;

  // ---- avoid polluting URL/body (config-only) ----
  delete p.max_page_size;

  return stripUndefined(p);
};

const normalizeOptionsParams = (raw) => {
  const p = { ...(raw || {}) };

  // options scoping also uses unit_ids
  if (p.unit_ids === undefined && p.units !== undefined) p.unit_ids = p.units;
  delete p.units;

  p.unit_ids = normalizeArray(p.unit_ids);
  p.ui_mode = normalizeString(p.ui_mode);
  p.view_mode = normalizeString(p.view_mode);
  p.date_mode = normalizeString(p.date_mode);
  p.unit_scope = normalizeString(p.unit_scope);
  p.units_mode = normalizeString(p.units_mode);
  p.primary_dataset = normalizeString(p.primary_dataset);

  // UI-only
  delete p.advanced_datasets;
  delete p.openDropdown;
  delete p.selected_unit;
  delete p.selected_units;

  delete p.max_page_size;

  return stripUndefined(p);
};

/**
 * DRF QueryDict getlist('x') formatı: x=a&x=b
 * Axios default array serialize bazen x[]=a üretir => burada repeat-key garanti ediyoruz.
 */
const reportParamsSerializer = (params) => {
  const cleaned = stripUndefined(params || {});
  const sp = new URLSearchParams();

  Object.entries(cleaned).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null) return;
        sp.append(key, String(item));
      });
      return;
    }

    if (typeof value === "object") {
      sp.append(key, JSON.stringify(value));
      return;
    }

    sp.append(key, String(value));
  });

  return sp.toString();
};

const buildUnitHeaders = (unitCtx) => {
  const headers = {};

  if (unitCtx?.unitId !== undefined && unitCtx?.unitId !== null) {
    headers["X-Unit-Id"] = String(unitCtx.unitId);
  }

  if (unitCtx?.unitSlug) {
    headers["X-Unit-Slug"] = String(unitCtx.unitSlug);
  }

  return headers;
};

export const extractFilenameFromResponse = (response, fallback = "rapor.xlsx") => {
  try {
    const cd =
      response?.headers?.["content-disposition"] ||
      response?.headers?.["Content-Disposition"];

    if (!cd) return fallback;

    const utf8Match = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].trim().replace(/(^")|("$)/g, ""));
    }

    const match = cd.match(/filename\s*=\s*("?)([^"]+)\1/i);
    if (match?.[2]) return match[2].trim();

    return fallback;
  } catch {
    return fallback;
  }
};

/* =============================================================================
   REPORT V1 ENDPOINTS
============================================================================= */

export const fetchReportsConfig = async () => {
  log("fetchReportsConfig:start");

  try {
    const { data } = await belediyeApi.get("/reports/config/");
    log("fetchReportsConfig:success", { contract_version: data?.contract_version });
    return data;
  } catch (err) {
    logError("fetchReportsConfig:error", err?.response?.status, err?.message);
    throw err;
  }
};

export const fetchReportsOptions = async (params) => {
  const normalized = normalizeOptionsParams(params);
  log("fetchReportsOptions:start", compact(normalized));

  try {
    const { data } = await belediyeApi.get("/reports/options/", {
      params: normalized,
      paramsSerializer: reportParamsSerializer,
    });

    log("fetchReportsOptions:success", {
      units: data?.units?.options?.length,
      neighborhoods: data?.neighborhoods?.options?.length,
      statuses: data?.statuses?.options?.length,
      datasets_allowed: data?.datasets_allowed,
    });

    return data;
  } catch (err) {
    logError(
      "fetchReportsOptions:error",
      err?.response?.status,
      err?.message,
      compact(normalized)
    );
    throw err;
  }
};

export const fetchReportsQuery = async (params, unitCtx, requestOptions = {}) => {
  const normalized = normalizeQueryExportParams(params);
  const headers = buildUnitHeaders(unitCtx);
  const method = normalizeMethod(requestOptions?.method);

  log("fetchReportsQuery:start", {
    method,
    params: compact(normalized),
    headers,
  });

  if (DEBUG) {
    const qs = reportParamsSerializer(normalized);
    log("fetchReportsQuery:qs", qs);
  }

  try {
    let data;

    if (method === "post") {
      ({ data } = await belediyeApi.post("/reports/query/", normalized, {
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      }));
    } else {
      ({ data } = await belediyeApi.get("/reports/query/", {
        params: normalized,
        paramsSerializer: reportParamsSerializer,
        headers,
      }));
    }

    const tables = Array.isArray(data?.tables) ? data.tables : [];
    log("fetchReportsQuery:success", {
      method,
      tables: tables.map((t) => t?.dataset || t?.name || t?.key).filter(Boolean),
      ui_mode: data?.effective_filters?.ui_mode,
      primary_dataset: data?.effective_filters?.primary_dataset,
    });

    return data;
  } catch (err) {
    logError(
      "fetchReportsQuery:error",
      method,
      err?.response?.status,
      err?.message,
      compact(normalized)
    );
    throw err;
  }
};

export const downloadReportsXlsx = async (params, unitCtx, requestOptions = {}) => {
  const normalized = normalizeQueryExportParams(params);
  const headers = buildUnitHeaders(unitCtx);
  const method = normalizeMethod(requestOptions?.method);

  log("downloadReportsXlsx:start", {
    method,
    params: compact(normalized),
    headers,
  });

  if (DEBUG) {
    const qs = reportParamsSerializer(normalized);
    log("downloadReportsXlsx:qs", qs);
  }

  try {
    let response;

    if (method === "post") {
      response = await belediyeApi.post("/reports/export.xlsx", normalized, {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      });
    } else {
      response = await belediyeApi.get("/reports/export.xlsx", {
        params: normalized,
        paramsSerializer: reportParamsSerializer,
        responseType: "blob",
        headers,
      });
    }

    log("downloadReportsXlsx:success", {
      method,
      status: response?.status,
      size: response?.data?.size,
      contentType: response?.headers?.["content-type"],
    });

    return response;
  } catch (err) {
    logError(
      "downloadReportsXlsx:error",
      method,
      err?.response?.status,
      err?.message,
      compact(normalized)
    );
    throw err;
  }
};

export const downloadReportsXlsxWithFilename = async (
  params,
  unitCtx,
  requestOptions = {}
) => {
  const response = await downloadReportsXlsx(params, unitCtx, requestOptions);
  const filename = extractFilenameFromResponse(response, "rapor.xlsx");
  return { response, filename, blob: response.data };
};

/* =============================================================================
   Async export (③) — start / status / download
   Büyük raporlar için: iş worker'da üretilir, gunicorn/gateway timeout'una
   takılmaz. Filtreler senkron export ile AYNI query-param serileştirmesiyle
   gönderilir (kanıtlanmış yol).
============================================================================= */

export const startReportExport = async (params, unitCtx) => {
  const normalized = normalizeQueryExportParams(params);
  const headers = buildUnitHeaders(unitCtx);

  log("startReportExport:start", { params: compact(normalized) });

  const response = await belediyeApi.post("/reports/export/start", null, {
    params: normalized,
    paramsSerializer: reportParamsSerializer,
    headers,
  });

  return response?.data || {}; // { job_id, state }
};

export const getReportExportStatus = async (jobId) => {
  const response = await belediyeApi.get("/reports/export/status", {
    params: { job_id: jobId },
  });
  return response?.data || {}; // { state, percent, message, filename, download_url, error }
};

export const downloadReportExportById = async (jobId) => {
  const response = await belediyeApi.get("/reports/export/download", {
    params: { job_id: jobId },
    responseType: "blob",
  });
  const filename = extractFilenameFromResponse(response, "rapor.xlsx");
  return { response, filename, blob: response.data };
};